// views/Admin/Bonuses/AdminBonuses.tsx
import { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../../components/common/Card/Card';
import Button from '../../../components/common/Button/Button';
import Modal from '../../../components/common/Modal/Modal';
import Loading from '../../../components/common/Loading/Loading';
import BonusForm from './components/BonusForm/BonusForm';
import FilterButton from './components/FilterButton/FilterButton';
import { Bonus, BonusType } from '../../../Entities';
import { notify } from '../../../components/common/Toast/toastBridge';
import styles from './AdminBonuses.module.css';

type Action = 'add' | 'edit' | 'duplicate';

const AdminBonuses = () => {
  const [showAddModal, setShowAddModal] = useState(false);
  const [editingBonus, setEditingBonus] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [bonuses, setBonuses] = useState<any[]>([]);
  const [itemsPerPage, setItemsPerPage] = useState(20);
  const [activeTypeFilters, setActiveTypeFilters] = useState<string[]>([]);
  const [availableTypes] = useState<string[]>(BonusType.values.map((t) => t.name as string));
  const [renewingFilter, setRenewingFilter] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const navigate = useNavigate();
  const actionRef = useRef<Action>('edit');

  const getBonuses = useCallback(
    async (page: number, currentItemsPerPage: number) => {
      try {
        setLoading(true);
        const fetched = await Bonus.getAllAdmin({
          page,
          itemsPerPage: currentItemsPerPage,
          bonusTypeFilters: activeTypeFilters.length ? activeTypeFilters : availableTypes,
          renewingFilter,
        });
        if (page === totalPages && fetched.length === currentItemsPerPage) {
          setTotalPages((prev) => prev + 1);
        }
        setBonuses(fetched);
        setLoading(false);
      } catch (error) {
        console.error(error);
        notify.error('Nepodařilo se načíst bonusy');
        navigate('/dashboard');
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [activeTypeFilters, renewingFilter, availableTypes, totalPages, navigate],
  );

  useEffect(() => {
    getBonuses(currentPage, itemsPerPage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTypeFilters, renewingFilter]);

  const handlePaginationChange = async (newPage: number, newItemsPerPage: number) => {
    setCurrentPage(newPage);
    await getBonuses(newPage, newItemsPerPage);
  };

  const handleAddBonus = async (formData: any) => {
    try {
      await Bonus.create(formData);
      await getBonuses(currentPage, itemsPerPage);
      setShowAddModal(false);
    } catch (error) {
      console.error(error);
      notify.error('Nastala chyba při přidávání bonusu');
      throw error;
    }
  };

  const handleUpdateBonus = async (bonus: any) => {
    try {
      const updatedBonus = new Bonus(bonus);
      await updatedBonus.update();
      setBonuses((prev) => prev.map((b) => (b.equals(updatedBonus) ? updatedBonus : b)));
      setShowAddModal(false);
    } catch (error) {
      console.error(error);
      notify.error('Nastala chyba při změně bonusu');
      throw error;
    }
  };

  const handleToggleVisible = async (id: number) => {
    try {
      setProcessing(true);
      const bonus = bonuses.find((b) => b.id === id);
      await handleUpdateBonus({ ...bonus, visible: !bonus.visible });
    } catch {
      /* surfaced by handleUpdateBonus */
    } finally {
      setProcessing(false);
    }
  };

  const handleEditBonus = (bonus: any) => {
    actionRef.current = 'edit';
    setEditingBonus(bonus);
    setShowAddModal(true);
  };
  const handleDuplicateBonus = (bonus: any) => {
    actionRef.current = 'duplicate';
    setEditingBonus(bonus);
    setShowAddModal(true);
  };
  const handleAddingBonus = () => {
    actionRef.current = 'add';
    setEditingBonus(null);
    setShowAddModal(true);
  };

  const toggleTypeFilter = (type: string) => {
    setCurrentPage(1);
    setTotalPages(1);
    setActiveTypeFilters((prev) => (prev.includes(type) ? prev.filter((t) => t !== type) : [...prev, type]));
  };

  if (loading) return <Loading />;

  return (
    <div className={styles.adminBonuses}>
      <div className={styles.header}>
        <h1>Správa bonusů</h1>
        <Button variant="primary" onClick={handleAddingBonus}>
          ➕ Přidat nový bonus
        </Button>
      </div>

      <Card className={styles.tableCard}>
        <div className={styles.controls}>
          <div className={styles.filtersGroup}>
            {availableTypes.map((type) => {
              const typeInfo = BonusType.get({ name: type });
              return (
                <FilterButton
                  key={type}
                  name={`${typeInfo.icon} ${typeInfo.label}`}
                  onClick={() => toggleTypeFilter(type)}
                  active={activeTypeFilters.includes(type)}
                  color={typeInfo.color}
                />
              );
            })}
          </div>

          <div className={styles.filtersGroup}>
            <FilterButton name="Renewing" onClick={() => setRenewingFilter((prev) => !prev)} active={renewingFilter} color="#FF8C00" />
          </div>

          <div className={styles.filtersGroup}>
            <select
              className="form-input"
              value={itemsPerPage}
              onChange={(e) => {
                setItemsPerPage(Number(e.target.value));
                setCurrentPage(1);
                handlePaginationChange(1, Number(e.target.value));
              }}
            >
              <option value={10}>10 na stránku</option>
              <option value={20}>20 na stránku</option>
              <option value={50}>50 na stránku</option>
              <option value={100}>100 na stránku</option>
            </select>
          </div>
        </div>

        {/* Desktop table */}
        <div className={styles.tableWrapper}>
          <table className={styles.table}>
            <thead>
              <tr>
                <th>ID</th>
                <th>Název</th>
                <th>Společnost</th>
                <th>Hodnota</th>
                <th>Riziko</th>
                <th>Platnost do</th>
                <th>Visible</th>
                <th>Akce</th>
              </tr>
            </thead>
            <tbody>
              {bonuses.map((bonus) => (
                <tr key={bonus.id}>
                  <td>{bonus.id}</td>
                  <td>{bonus.name}</td>
                  <td>{bonus.company.name}</td>
                  <td>{bonus.expected_value} Kč</td>
                  <td>{bonus.risk_rate}</td>
                  <td>{new Date(bonus.end_timestamp * 1000).toLocaleDateString('cs-CZ')}</td>
                  <td>
                    <span className={`${styles.visibleBadge} ${styles[String(bonus.visible)]}`}>{bonus.visible ? 'Aktivní' : 'Neaktivní'}</span>
                  </td>
                  <td>
                    <div className={styles.actions}>
                      <button className={`${styles.actionBtn} ${styles.edit}`} onClick={() => handleEditBonus(bonus)} title="Upravit">
                        ✏️
                      </button>
                      <button className={styles.actionBtn} onClick={() => handleToggleVisible(bonus.id)} title={bonus.visible ? 'Deaktivovat' : 'Aktivovat'} disabled={processing}>
                        {bonus.visible ? '⏸️' : '▶️'}
                      </button>
                      <button className={styles.actionBtn} onClick={() => handleDuplicateBonus(bonus)} title="Duplikovat" disabled={processing}>
                        📄
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Mobile cards */}
        <div className={styles.mobileCardsContainer}>
          {bonuses.map((bonus) => (
            <div key={bonus.id} className={styles.mobileCard}>
              <div className={styles.mobileCardHeader}>
                <div className={styles.mobileCardTitle}>{bonus.name}</div>
                <div className={styles.mobileCardId}>#{bonus.id}</div>
              </div>
              <div className={styles.mobileCardBody}>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Společnost</span>
                  <span className={styles.mobileCardValue}>{bonus.company.name}</span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Hodnota</span>
                  <span className={styles.mobileCardValue}>{bonus.expected_value} Kč</span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Riziko</span>
                  <span className={styles.mobileCardValue}>{bonus.risk_rate}</span>
                </div>
                <div className={styles.mobileCardRow}>
                  <span className={styles.mobileCardLabel}>Platnost do</span>
                  <span className={styles.mobileCardValue}>{new Date(bonus.end_timestamp * 1000).toLocaleDateString('cs-CZ')}</span>
                </div>
              </div>
              <div className={styles.mobileCardFooter}>
                <span className={`${styles.visibleBadge} ${styles[String(bonus.visible)]}`}>{bonus.visible ? 'Aktivní' : 'Neaktivní'}</span>
                <div className={styles.mobileCardActions}>
                  <button className={`${styles.actionBtn} ${styles.edit}`} onClick={() => handleEditBonus(bonus)} title="Upravit">
                    ✏️
                  </button>
                  <button className={styles.actionBtn} onClick={() => handleToggleVisible(bonus.id)} title={bonus.visible ? 'Deaktivovat' : 'Aktivovat'} disabled={processing}>
                    {bonus.visible ? '⏸️' : '▶️'}
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Pagination */}
        <div className={styles.pagination}>
          <div className={styles.paginationInfo} />
          <div className={styles.paginationControls}>
            <Button variant="secondary" onClick={() => handlePaginationChange(Math.max(currentPage - 1, 1), itemsPerPage)} disabled={currentPage === 1}>
              ← Předchozí
            </Button>
            <div className={styles.pageNumbers}>
              {Array.from({ length: totalPages }, (_, i) => {
                const pageNum = i + 1;
                return (
                  <button key={pageNum} className={`${styles.pageButton} ${currentPage === pageNum ? styles.active : ''}`} onClick={() => handlePaginationChange(pageNum, itemsPerPage)}>
                    {pageNum}
                  </button>
                );
              })}
            </div>
            <Button variant="secondary" onClick={() => handlePaginationChange(currentPage + 1, itemsPerPage)} disabled={currentPage === totalPages}>
              Další →
            </Button>
          </div>
        </div>
      </Card>

      <Modal
        isOpen={showAddModal}
        onClose={() => {
          setShowAddModal(false);
          setEditingBonus(null);
        }}
        title={editingBonus ? 'Upravit bonus' : 'Přidat nový bonus'}
        size="large"
      >
        <BonusForm
          action={actionRef.current}
          bonus={editingBonus}
          onBonusAdd={handleAddBonus}
          onBonusUpdate={handleUpdateBonus}
          onCancel={() => {
            setShowAddModal(false);
            setEditingBonus(null);
          }}
        />
      </Modal>
    </div>
  );
};

export default AdminBonuses;
